import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  EventEmitter,
  Renderer2,
} from '@angular/core';
import { AnnotationListModel } from '../../../core/models/consultation/annotation-list.model';
import { ConsultationsService } from '../../../core/services/consultation/consultations.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CaseStatus } from '../../../../enums/case-status.enum';
import { CurrentUserService } from '../../../core/services/user/current-user.service';
import { UserSessionModel } from '../../../core/models/user/current-user.model';
import { UserRole } from '../../../../enums/roles.enum';
import {
  BENIGN_SKIN_CONDITIONS,
  MALIGNANT_SKIN_CONDITIONS,
  PRE_MALIGNANT_SKIN_CONDITIONS,
} from '../../constants/constants';

interface CoordinatesModel {
  x: number;
  y: number;
}

@Component({
  selector: 'app-img-editor',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './img-editor.component.html',
  styleUrl: './img-editor.component.scss',
})
export class ImgEditorComponent implements AfterViewInit, OnChanges, OnDestroy {
  constructor(
    private consultationsService: ConsultationsService,
    private translate: TranslateService,
    private currentUserService: CurrentUserService,
    private renderer2: Renderer2
  ) {
    const userLang = localStorage.getItem('selectedLanguage') || 'en';
    this.translate.use(userLang);
  }
  @ViewChild('canvas', { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>;
  @Input() src: string;
  @Input() patientRecordCaseImageId: string;
  @Input() persistAnnotations = true;
  @Input() caseDetailStatus: number;
  @Input() selectedDiagnosis: string;
  benignSkinConditions = BENIGN_SKIN_CONDITIONS;
  preMalignantSkinConditions = PRE_MALIGNANT_SKIN_CONDITIONS;
  malignantSkinConditions = MALIGNANT_SKIN_CONDITIONS;

  @Output() annotationListChange = new EventEmitter<any>();

  @Input() annotationList: AnnotationListModel[] = [];
  private subscriptions: Subscription = new Subscription();
  private resizeObserver!: ResizeObserver;
  private ctx!: CanvasRenderingContext2D;
  private image = new Image();
  private scale: number;
  private maxScale: number;
  private scaleFactor: number;
  private animationDuration: number = 75; //ms
  private ratio!: number;
  private moveX: number;
  private moveY: number;
  private isDragging: boolean;
  private startCoords: CoordinatesModel;
  private last: CoordinatesModel;
  public imgCoordinates: CoordinatesModel;
  private isResizing: boolean = false;
  private resizeStartCoords: CoordinatesModel | null = null;
  private rectToResize: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null = null;
  // private isFullScreen: boolean = false;
  annotationTextList: {
    annotationText: string;
    textX: number;
    textY: number;
    textWidth: number;
    textHeight: number;
  }[] = [];

  /**
   * Drawing START
   */
  private isDrawing: boolean = false;
  private rectStart: CoordinatesModel | null = null;
  private currentRect: {
    x: number;
    y: number;
    width: number;
    height: number;
    selectedAnnotationId?: string;
  } | null = null;
  private drawnRects: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
    selectedAnnotationId?: string;
  }> = [];
  isDrawingMode: boolean = false;
  actionType: 'drawing' | 'dragging' | 'resizing' | 'moveRect';
  private isMovingRect: boolean = false;
  private rectToMove: any | null = null;
  private moveStartCoords: CoordinatesModel | null = null;
  isDeleteBtnClicked: boolean = false;
  private saveRectsInterval: any;
  selectedAnnotation: any = null;
  caseStatus = CaseStatus;
  currentUser: UserSessionModel = null;
  userRole: any = UserRole;
  isFullScreenActivated: boolean = false;

  /**
   * Default values
   */
  private reset() {
    this.scale = 1;
    this.maxScale = 2;
    this.scaleFactor = 1.5;
    this.moveX = 0;
    this.moveY = 0;
    this.isDragging = false;
    this.startCoords = { x: 0, y: 0 };
    this.last = { x: 0, y: 0 };
    this.isDrawing = false;
    this.rectStart = null;
    this.currentRect = null;
    this.drawnRects = [];
    this.initImg();
  }

  ngOnInit(): void {
    this.currentUser = this.currentUserService.GetSession();
    if (this.patientRecordCaseImageId && this.persistAnnotations) {
      this.getRectanglesWithAnnotations(this.patientRecordCaseImageId);
    } else if (!this.persistAnnotations) {
      this.getAnnotationList();
    }

    this.renderer2.listen(document, 'fullscreenchange', () => {
      if (document.fullscreenElement) {
        this.isFullScreenActivated = true;
      } else {
        this.isFullScreenActivated = false;
      }
    });
  }

  ngAfterViewInit(): void {
    this.setEditorAction('dragging');
    this.resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect) {
          this.initImg();
        }
      }
    });
    this.resizeObserver.observe(this.canvasRef.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes['src']) this.reset();
    if (
      changes['patientRecordCaseImageId'] &&
      this.persistAnnotations &&
      changes['patientRecordCaseImageId'].currentValue &&
      changes['patientRecordCaseImageId'].currentValue !==
        changes['patientRecordCaseImageId'].previousValue
    ) {
      this.getRectanglesWithAnnotations(
        changes['patientRecordCaseImageId'].currentValue
      );
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.subscriptions.unsubscribe();
    if (this.saveRectsInterval) {
      clearInterval(this.saveRectsInterval);
    }
  }

  @HostListener('window:resize')
  onResize() {
    // const _isFullScreen: boolean = !!document.fullscreenElement;
    // if (this.isFullScreen !== _isFullScreen) {
    //   this.isFullScreen = _isFullScreen;
    // }
    this.initImg();
  }

  getAnnotationList(): void {
    this.consultationsService.annotationList().subscribe({
      next: (res) => {
        this.annotationList = res;
        const myEvent = {
          annotationList: this.annotationList,
          drawnRects: this.drawnRects,
        };
        this.annotationListChange.emit(myEvent);
      },
    });
  }

  getRectanglesWithAnnotations(id: string): void {
    this.consultationsService.rectanglesWithAnnotations(id).subscribe({
      next: (res) => {
        this.drawnRects = res.map((rect: any) => {
          return {
            x: rect.x1,
            y: rect.y1,
            width: rect.x2 - rect.x1,
            height: rect.y2 - rect.y1,
            selectedAnnotationId: rect.annotationId,
          };
        });
        this.drawRects();
        this.getAnnotationList();
      },
    });
  }

  private initImg() {
    const canvas = this.canvasRef.nativeElement;
    canvas.height = 0;
    this.ctx = canvas.getContext('2d')!;
    this.image.src = this.src;
    this.image.onload = () => {
      this.resizeCanvas();
    };
  }

  private resizeCanvas() {
    const canvasContainer = document.querySelector(
      '.canvas-container'
    ) as HTMLElement;
    const canvas = this.canvasRef.nativeElement;
    canvas.width = canvasContainer.offsetWidth;
    canvas.height = canvasContainer.offsetHeight;
    this.ratio = Math.max(
      canvas.height / this.image.height,
      canvas.width / this.image.width
    );
    const _maxScale = Math.min(
      this.image.height / canvas.height,
      this.image.width / canvas.width
    );
    this.maxScale = _maxScale > 1 ? _maxScale : this.maxScale;

    this.drawCanvas();
    this.drawRect(this.currentRect);
  }

  enableDrawingMode() {
    this.isDrawingMode = true;
    this.isDragging = false;
  }

  private drawCanvas() {
    if (!this.ctx) {
      return;
    }
    const canvas = this.canvasRef.nativeElement;
    const imageWidth = this.image.width * this.ratio * this.scale;
    const imageHeight = this.image.height * this.ratio * this.scale;
    const offsetX = (imageWidth - canvas.width) / 2;
    const offsetY = (imageHeight - canvas.height) / 2;

    this.ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Limit movement
    this.moveX = Math.max(
      Math.min(this.moveX, offsetX),
      -(imageWidth - offsetX - canvas.width)
    );
    this.moveY = Math.max(
      Math.min(this.moveY, offsetY),
      -(imageHeight - offsetY - canvas.height)
    );

    this.ctx.drawImage(
      this.image,
      -offsetX + this.moveX,
      -offsetY + this.moveY,
      imageWidth,
      imageHeight
    );
    this.drawRects();
    setTimeout(() => {
      this.drawAnnotations();
    }, 500);
  }

  onScale(direction: 'up' | 'down') {
    const startScale = this.scale;
    const endScale =
      direction === 'up'
        ? this.scale * this.scaleFactor
        : this.scale / this.scaleFactor;
    const targetScale = Math.min(Math.max(endScale, 1), this.maxScale);
    this.animateZoom(startScale, targetScale, direction);
  }

  animateZoom(
    startScale: number,
    targetScale: number,
    direction: 'up' | 'down'
  ) {
    const startTime = performance.now();
    const animate = (currentTime: number) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / this.animationDuration, 1);
      let scale: number;
      if (direction === 'up') {
        scale = startScale + Math.abs(targetScale - startScale) * progress;
        scale = Math.max(scale, startScale);
        scale = Math.min(scale, targetScale);
      } else {
        scale = startScale - Math.abs(startScale - targetScale) * progress;
        scale = Math.min(scale, startScale);
        scale = Math.max(scale, targetScale);
      }
      this.scale = scale;
      this.drawCanvas();

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  onMouseDown(event: MouseEvent) {
    if (this.actionType === 'drawing') {
      this.onMouseDownForDrawing(event);
    } else if (this.actionType === 'dragging') {
      this.onMouseDownForDragging(event);
    } else if (this.actionType === 'resizing') {
      this.onMouseDownForResizing(event);
    } else if (this.actionType === 'moveRect') {
      this.onMouseDownForMovingRect(event);
    }
  }

  onMouseDownForDrawing(event: MouseEvent) {
    this.isDrawing = true;
    this.rectStart = this.getCoordinates(event);
  }

  onMouseDownForDragging(event: MouseEvent) {
    this.isDragging = true;
    this.startCoords = {
      x: event.offsetX - this.last.x,
      y: event.offsetY - this.last.y,
    };
  }

  onMouseDownForResizing(event: MouseEvent) {
    this.isResizing = true;
    this.rectToResize = this.getRectangleAtCoordinates(event);
    this.resizeStartCoords = this.getCoordinates(event);
  }

  private onMouseDownForMovingRect(event: MouseEvent) {
    this.rectToMove = this.getRectangleAtCoordinates(event);
    if (this.rectToMove) {
      this.isMovingRect = true;
      this.moveStartCoords = this.getCoordinates(event);
    }
  }

  private getRectangleAtCoordinates(
    event: MouseEvent
  ): { x: number; y: number; width: number; height: number } | null {
    const coords = this.getCoordinates(event);
    return (
      this.drawnRects.find(
        (rect) =>
          coords.x >= rect.x &&
          coords.x <= rect.x + rect.width &&
          coords.y >= rect.y &&
          coords.y <= rect.y + rect.height
      ) || null
    );
  }

  onMouseMove(event: MouseEvent) {
    if (this.isDrawing && this.rectStart) {
      return this.onMouseMoveForDrawing(event);
    }
    if (this.isDragging) {
      return this.onMouseMoveForDragging(event);
    }
    if (this.isResizing && this.rectToResize) {
      return this.onMouseMoveForResizing(event);
    }

    if (this.isMovingRect && this.rectToMove && this.moveStartCoords) {
      const currentCoords = this.getCoordinates(event);
      const deltaX = currentCoords.x - this.moveStartCoords.x;
      const deltaY = currentCoords.y - this.moveStartCoords.y;

      this.rectToMove.x += deltaX;
      this.rectToMove.y += deltaY;

      this.moveStartCoords = currentCoords;
      this.drawCanvas();
    }
  }

  onMouseMoveForResizing(event: MouseEvent) {
    const currentCoords = this.getCoordinates(event);
    const deltaX = currentCoords.x - this.resizeStartCoords.x;
    const deltaY = currentCoords.y - this.resizeStartCoords.y;

    const newWidth = this.rectToResize.width + deltaX;
    const newHeight = this.rectToResize.height + deltaY;

    if (newWidth > 0) {
      this.rectToResize.width = newWidth;
    }
    if (newHeight > 0) {
      this.rectToResize.height = newHeight;
    }

    this.resizeStartCoords = currentCoords;

    this.drawCanvas();
  }

  onMouseMoveForDrawing(event: MouseEvent) {
    const currentCoords = this.getCoordinates(event);

    const newWidth = currentCoords.x - this.rectStart.x;
    const newHeight = currentCoords.y - this.rectStart.y;

    if (newWidth < 0 || newHeight < 0) {
      return;
    }

    this.currentRect = {
      x: this.rectStart.x,
      y: this.rectStart.y,
      width: newWidth,
      height: newHeight,
    };

    this.drawCanvas();
    this.drawRect(this.currentRect);
  }

  onMouseMoveForDragging(event: MouseEvent) {
    this.moveX = event.offsetX - this.startCoords.x;
    this.moveY = event.offsetY - this.startCoords.y;
    this.drawCanvas();
  }

  onMouseUp() {
    if (this.isDrawing && this.currentRect) {
      return this.onMouseUpForDrawing();
    } else if (this.isDragging) {
      return this.onMouseUpForDragging();
    } else if (this.isResizing) {
      return this.onMouseUpForResizing();
    } else if (this.isMovingRect) {
      setTimeout(() => {
        this.isMovingRect = false;
        this.rectToMove = null;
        this.moveStartCoords = null;
        this.saveRectangles();
      }, 100);
    }
  }

  onMouseUpForResizing() {
    this.isResizing = false;
    this.rectToResize = null;
    this.saveRectangles();
  }

  onMouseUpForDrawing() {
    this.saveRectangle(this.currentRect);
    if (this.currentRect) {
      this.addComboBoxToRectangle(this.currentRect);
    }
    this.rectStart = null;
    this.currentRect = null;
  }

  onMouseUpForDragging() {
    this.isDragging = false;
    this.last = { x: this.moveX, y: this.moveY };
  }

  private addComboBoxToRectangle(rect: {
    x: number;
    y: number;
    width: number;
    height: number;
    selectedAnnotationId?: string;
  }) {
    const canvas = this.canvasRef.nativeElement;
    const canvasRect = canvas.getBoundingClientRect();

    const imageWidth = this.image.width * this.ratio * this.scale;
    const imageHeight = this.image.height * this.ratio * this.scale;

    const offsetX = (imageWidth - canvas.width) / 2 - this.moveX;
    const offsetY = (imageHeight - canvas.height) / 2 - this.moveY;

    const canvasX = rect?.x * (this.ratio * this.scale) - offsetX;
    const canvasY =
      rect?.y * (this.ratio * this.scale) -
      offsetY +
      rect?.height * (this.ratio * this.scale);

    const domX = canvasRect.left + canvasX - 4;
    const domY = this.isFullScreenActivated
      ? canvasRect.top + canvasY - 30
      : canvasRect.top + canvasY + 10;

    const comboBox = document.createElement('select');

    const placeholderOption = document.createElement('option');
    placeholderOption.text = this.translate.instant('selectAnAnnotation');
    placeholderOption.value = '';
    placeholderOption.selected = true;
    placeholderOption.disabled = true;
    comboBox.appendChild(placeholderOption);

    this.annotationList.forEach((annotation) => {
      const option = document.createElement('option');
      option.value = annotation.annotationId;
      option.text = annotation.annotationName;
      comboBox.appendChild(option);
    });

    comboBox.style.position = 'absolute';
    comboBox.style.left = `${domX}px`;
    comboBox.style.top = `${domY}px`;
    comboBox.style.border = '1px solid #ccc';
    comboBox.style.width = `${rect?.width * (this.ratio * this.scale)}px`;
    comboBox.style.padding = '8px';
    comboBox.style.zIndex = '9999';

    const container = this.isFullScreenActivated
      ? this.canvasRef.nativeElement.parentNode
      : document.body;

    container.appendChild(comboBox);

    const handleComboBoxChange = (event: Event) => {
      const selectedId = (event.target as HTMLSelectElement).value;
      this.selectedAnnotation = this.annotationList.find(
        (annotation) => annotation.annotationId === selectedId
      );

      if (this.selectedAnnotation) {
        rect.selectedAnnotationId = selectedId;
        container.removeChild(comboBox);
        this.drawAnnotations();
        this.drawCanvas();
        this.saveRectangles();
        const myEvent = {
          annotationList: this.annotationList,
          drawnRects: this.drawnRects,
        };
        this.annotationListChange.emit(myEvent);
      }
    };

    comboBox.addEventListener('change', handleComboBoxChange);

    const handleClickOutside = (event: MouseEvent) => {
      if (!comboBox.contains(event.target as Node)) {
        container.removeChild(comboBox);
        document.removeEventListener('click', handleClickOutside);
      }
    };

    setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    });
  }

  getCoordinates(event: MouseEvent): CoordinatesModel {
    if (this.isDragging) return this.imgCoordinates;
    const canvas = this.canvasRef.nativeElement;
    /**Canvas Coordinates */
    const canvasRect = canvas.getBoundingClientRect();
    const canvasX = event.clientX - canvasRect.left;
    const canvasY = event.clientY - canvasRect.top;
    /**Image Coordinates */
    const imageWidth = this.image.width * this.ratio * this.scale;
    const imageHeight = this.image.height * this.ratio * this.scale;
    const offsetX = (imageWidth - canvas.width) / 2 - this.moveX;
    const offsetY = (imageHeight - canvas.height) / 2 - this.moveY;

    const photoX = (canvasX + offsetX) / (this.ratio * this.scale);
    const photoY = (canvasY + offsetY) / (this.ratio * this.scale);
    this.imgCoordinates = {
      x: Number(photoX.toFixed()),
      y: Number(photoY.toFixed()),
    };
    return { x: Number(photoX.toFixed()), y: Number(photoY.toFixed()) };
  }

  //#region DRAWING
  /**
   * @param rect
   */
  private drawRect(rect: {
    x: number;
    y: number;
    width: number;
    height: number;
    selectedAnnotationId?: string;
  }) {
    const canvas = this.canvasRef.nativeElement;
    const imageWidth = this.image.width * this.ratio * this.scale;
    const imageHeight = this.image.height * this.ratio * this.scale;

    const offsetX = (imageWidth - canvas.width) / 2 - this.moveX;
    const offsetY = (imageHeight - canvas.height) / 2 - this.moveY;

    const canvasX = rect?.x * (this.ratio * this.scale) - offsetX;
    const canvasY = rect?.y * (this.ratio * this.scale) - offsetY;
    this.ctx.beginPath();
    this.ctx.rect(
      canvasX,
      canvasY,
      rect?.width * (this.ratio * this.scale),
      rect?.height * (this.ratio * this.scale)
    );
    this.ctx.strokeStyle = 'green';
    this.ctx.lineWidth = 2;
    this.ctx.setLineDash([10, 5]);
    this.ctx.stroke();
    this.ctx.setLineDash([]);
    this.ctx.closePath();

    // Draw X button
    const buttonSize = 30;
    const buttonRadius = buttonSize / 2;
    const buttonCenterX = canvasX - 15;
    const buttonCenterY = canvasY - 15;

    this.ctx.beginPath();
    this.ctx.fillStyle = 'green';
    this.ctx.arc(buttonCenterX, buttonCenterY, buttonRadius, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.closePath();

    // Draw X symbol
    this.ctx.beginPath();
    this.ctx.strokeStyle = 'white';
    this.ctx.lineWidth = 3;
    const offset = 5;
    this.ctx.moveTo(buttonCenterX - offset, buttonCenterY - offset);
    this.ctx.lineTo(buttonCenterX + offset, buttonCenterY + offset);
    this.ctx.moveTo(buttonCenterX + offset, buttonCenterY - offset);
    this.ctx.lineTo(buttonCenterX - offset, buttonCenterY + offset);
    this.ctx.stroke();
    this.ctx.closePath();

    if (this.caseDetailStatus < this.caseStatus.ReportSent) {
      canvas.addEventListener('click', (event) => {
        if (this.isDeleteBtnClicked) return;
        this.isDeleteBtnClicked = true;
        setTimeout(() => {
          this.isDeleteBtnClicked = false;
        }, 1000);
        const rect = canvas.getBoundingClientRect();
        const clickX = event.clientX - rect?.left;
        const clickY = event.clientY - rect?.top;

        for (const drawnRect of this.drawnRects) {
          const imageWidth = this.image.width * this.ratio * this.scale;
          const imageHeight = this.image.height * this.ratio * this.scale;

          const offsetX = (imageWidth - canvas.width) / 2 - this.moveX;
          const offsetY = (imageHeight - canvas.height) / 2 - this.moveY;

          const canvasX = drawnRect.x * (this.ratio * this.scale) - offsetX;
          const canvasY = drawnRect.y * (this.ratio * this.scale) - offsetY;

          const buttonCenterX = canvasX - 15;
          const buttonCenterY = canvasY - 15;
          const buttonRadius = 15;
          const extraPadding = 15;

          const distance = Math.sqrt(
            Math.pow(clickX - buttonCenterX, 2) +
              Math.pow(clickY - buttonCenterY, 2)
          );

          if (distance <= buttonRadius + extraPadding) {
            this.drawnRects = this.drawnRects.filter((r) => r !== drawnRect);

            const myEvent = {
              annotationList: this.annotationList,
              drawnRects: this.drawnRects,
            };

            this.annotationListChange.emit(myEvent);

            this.drawCanvas();

            this.saveRectangles();

            break;
          }
        }
      });
    }
  }

  private drawRects(): void {
    if (!this.ctx) return;
    this.drawnRects.forEach((rect) => this.drawRect(rect));
  }

  private drawAnnotations() {
    const canvas = this.canvasRef.nativeElement;
    const imageWidth = this.image.width * this.ratio * this.scale;
    const imageHeight = this.image.height * this.ratio * this.scale;

    const offsetX = (imageWidth - canvas.width) / 2 - this.moveX;
    const offsetY = (imageHeight - canvas.height) / 2 - this.moveY;

    this.drawnRects.forEach((rect, index) => {
      const canvasX = rect.x * (this.ratio * this.scale) - offsetX;
      const canvasY = rect.y * (this.ratio * this.scale) - offsetY;

      const annotationText = rect?.selectedAnnotationId
        ? this.annotationList.find(
            (a) => a.annotationId === rect?.selectedAnnotationId
          )?.annotationName || ''
        : '';

      // const textWidth = this.ctx.measureText(annotationText).width;
      // const textHeight = 16;

      this.ctx.font = '14px Poppins';
      this.ctx.fillStyle = 'green';
      const textX = canvasX;
      const textY = canvasY + rect.height * (this.ratio * this.scale) + 20;

      this.ctx.fillText(annotationText, textX, textY);

      canvas.addEventListener('click', (event) => {
        const rectBounds = {
          left: textX,
          top: textY - 14,
          right: textX + this.ctx.measureText(annotationText).width,
          bottom: textY,
        };

        if (
          event.offsetX >= rectBounds.left &&
          event.offsetX <= rectBounds.right &&
          event.offsetY >= rectBounds.top &&
          event.offsetY <= rectBounds.bottom &&
          !this.isMovingRect
        ) {
          this.addComboBoxToRectangle(rect);
        }
      });
    });
  }

  private saveRectangle(rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) {
    this.drawnRects.push(rect);
  }

  isEditorActionDisabled() {
    return (
      this.caseDetailStatus >= this.caseStatus.ReportSent ||
      this.currentUser?.role === this.userRole.OrganizationAdmin ||
      this.currentUser?.role === this.userRole.GeneralPractitioner ||
      !this.selectedDiagnosis ||
      this.selectedDiagnosis === this.benignSkinConditions ||
      this.selectedDiagnosis === this.preMalignantSkinConditions
    );
  }

  //#endregion

  setEditorAction(type: 'drawing' | 'dragging' | 'resizing' | 'moveRect') {
    this.actionType = type;

    const canvas = this.canvasRef.nativeElement;
    if (this.actionType === 'drawing') {
      canvas.classList.add('drawing');
      canvas.classList.remove('dragging', 'resizing', 'moveRect');
    } else if (this.actionType === 'dragging') {
      canvas.classList.add('dragging');
      canvas.classList.remove('drawing', 'resizing', 'moveRect');
    } else if (this.actionType === 'resizing') {
      canvas.classList.add('resizing');
      canvas.classList.remove('drawing', 'dragging', 'moveRect');
    } else if (this.actionType === 'moveRect') {
      canvas.classList.add('moveRect');
      canvas.classList.remove('drawing', 'dragging', 'resizing');
    }
  }

  undoDrawing() {
    this.drawnRects.pop();
    this.drawCanvas();
  }

  saveRectangles() {
    if (!this.persistAnnotations) {
      return;
    }

    const formattedRects = this.drawnRects.map((rect) => {
      return {
        x1: rect.x,
        y1: rect.y,
        x2: rect.x + rect.width,
        y2: rect.y + rect.height,
        annotationId: rect.selectedAnnotationId,
      };
    });

    const form = {
      patientRecordCaseImageId: this.patientRecordCaseImageId,
      caseImageAnnotations: formattedRects,
    };

    this.consultationsService.caseImageAnnotation(form).subscribe({
      next: async () => {},
    });
  }
}
